import { useCallback, useEffect, useRef, useState } from 'react';
import { settingsService, type Capabilities } from '@services/settings.service';

export type CapabilityStatus = 'loading' | 'ready' | 'error';

interface UseCapabilities {
  /** Undefined unless `status === 'ready'` — never a default or a stale payload. */
  capabilities: Capabilities | undefined;
  status: CapabilityStatus;
  retry: () => void;
}

/** The limits the server will enforce. On failure: `error` and no values — never a guess. */
export function useCapabilities(): UseCapabilities {
  const [capabilities, setCapabilities] = useState<Capabilities | undefined>(undefined);
  const [status, setStatus] = useState<CapabilityStatus>('loading');
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    setStatus('loading');
    // Dropped first: a payload from the previous attempt would outlive its failure.
    setCapabilities(undefined);

    settingsService
      .getCapabilities()
      .then((result) => {
        if (!current || !mounted.current) return;
        setCapabilities(result);
        setStatus('ready');
      })
      .catch(() => {
        if (!current || !mounted.current) return;
        setCapabilities(undefined);
        setStatus('error');
      });

    return () => {
      current = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { capabilities, status, retry };
}
